import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNodesState, useEdgesState } from "reactflow";
import storageHelper from '../utils/storageHelper.js';
import { auth } from '../firebase/config';
import { saveNode, createSession, getAllWorkflowsFromFirestore } from '../services/firestoreService';

// Start with empty nodes and edges
const initialNodes = [];
const initialEdges = [];

// Source node positioning configuration
const SOURCE_POSITIONING_CONFIG = {
  // Base offset from parent node top - positioned at 3/4 down the parent (75% of 250px = 187.5px ≈ 190px)
  topOffset: 190, // At 3/4 position down the parent node (75% of parent height)
  // Vertical spacing between stacked source nodes
  verticalSpacing: 300, // Reduced from 400px for better density
  // Horizontal distance to the left of parent
  horizontalOffset: 200, // Set to 200px for optimal positioning
  // Minimum clearance from other nodes
  minClearance: 100,
  // Additional spacing for expanded parent nodes
  expandedParentBuffer: 150,
};

// Smart Directional Positioning System
const POSITION_PRIORITY = [
  {
    direction: "right",
    shimmer: "left-to-right",
    sourceHandle: "source-right",
    targetHandle: "target-left",
    offset: { x: 1, y: 0 },
  },
  {
    direction: "top",
    shimmer: "bottom-to-top",
    sourceHandle: "source-top",
    targetHandle: "target-bottom",
    offset: { x: 0, y: -1 },
  },
  {
    direction: "bottom",
    shimmer: "top-to-bottom",
    sourceHandle: "source-bottom",
    targetHandle: "target-top",
    offset: { x: 0, y: 1 },
  },
  {
    direction: "left",
    shimmer: "right-to-left",
    sourceHandle: "source-left",
    targetHandle: "target-right",
    offset: { x: -1, y: 0 },
  },
  {
    direction: "upper-right",
    shimmer: "left-to-right",
    sourceHandle: "source-right",
    targetHandle: "target-left",
    offset: { x: 1, y: -1 },
  },
  {
    direction: "bottom-right",
    shimmer: "left-to-right",
    sourceHandle: "source-right",
    targetHandle: "target-left",
    offset: { x: 1, y: 1 },
  },
  {
    direction: "bottom-left",
    shimmer: "right-to-left",
    sourceHandle: "source-left",
    targetHandle: "target-right",
    offset: { x: -1, y: 1 },
  },
  {
    direction: "top-left",
    shimmer: "right-to-left",
    sourceHandle: "source-left",
    targetHandle: "target-right",
    offset: { x: -1, y: -1 },
  },
  {
    direction: "right-extended",
    shimmer: "left-to-right",
    sourceHandle: "source-right",
    targetHandle: "target-left",
    offset: { x: 2, y: 0 },
  },
];

// Helper function to calculate actual node height based on content and expansion state
const calculateActualNodeHeight = (node) => {
  if (!node?.data?.response) return 250; // Default height if no content

  const responseText = node.data.response;
  const isExpanded = node.data?.isExpanded || false;
  const lineCount = responseText.split("\n").length;
  const estimatedLineHeight = 24;
  const headerHeight = 80;
  const paddingAndMargin = 60;
  const askButtonHeight = 60;
  const sourceSectionHeight = 40; // Approximate height for sources section
  const quickActionsHeight = 40; // Approximate height for quick actions

  if (isExpanded) {
    // For expanded nodes, calculate based on actual full content
    const contentHeight = lineCount * estimatedLineHeight;
    const totalHeight =
      headerHeight +
      contentHeight +
      paddingAndMargin +
      askButtonHeight +
      sourceSectionHeight +
      quickActionsHeight;
    return Math.max(totalHeight, 250); // Ensure minimum height
  } else {
    // For collapsed nodes, use preview height (5 lines max)
    const previewLines = Math.min(lineCount, 5);
    const contentHeight = previewLines * estimatedLineHeight;
    const totalHeight =
      headerHeight + contentHeight + paddingAndMargin + askButtonHeight;
    return Math.max(totalHeight, 250); // Ensure minimum height
  }
};

// Smart positioning utility functions
const checkPositionAvailable = (
  testPosition,
  existingPositions,
  nodeWidth = 350,
  nodeHeight = 250,
  minSpacing = 200
) => {
  const clearanceBuffer = minSpacing;

  // Return true if NO overlaps are found (position is available)
  return !existingPositions.some((existingPos) => {
    // Check if testPosition overlaps with existingPos
    const overlaps = !(
      testPosition.x + nodeWidth + clearanceBuffer <= existingPos.x ||
      existingPos.x + nodeWidth + clearanceBuffer <= testPosition.x ||
      testPosition.y + nodeHeight + clearanceBuffer <= existingPos.y ||
      existingPos.y + nodeHeight + clearanceBuffer <= testPosition.y
    );
    return overlaps;
  });
};

const calculatePositionCoordinates = (
  parentPosition,
  direction,
  nodeWidth = 350,
  nodeHeight = 250,
  minSpacing = 200,
  parentNode = null
) => {
  const positionConfig = POSITION_PRIORITY.find(
    (p) => p.direction === direction
  );
  if (!positionConfig) return parentPosition;

  const spacing = nodeWidth + minSpacing;

  // Calculate dynamic height based on parent node's expansion state and content
  let dynamicHeight = nodeHeight;
  if (
    parentNode &&
    (direction === "top" ||
      direction === "bottom" ||
      direction.includes("top") ||
      direction.includes("bottom"))
  ) {
    dynamicHeight = calculateActualNodeHeight(parentNode);
  }

  const verticalSpacing = dynamicHeight + minSpacing + 100; // Extra 100px for vertical spacing

  let x = parentPosition.x;
  let y = parentPosition.y;

  // Calculate position based on offset multipliers
  if (positionConfig.offset.x === 1) x += spacing; // Right
  else if (positionConfig.offset.x === -1) x -= spacing; // Left
  else if (positionConfig.offset.x === 2) x += spacing * 1.5; // Extended right

  if (positionConfig.offset.y === 1) y += verticalSpacing; // Bottom
  else if (positionConfig.offset.y === -1) y -= verticalSpacing; // Top

  return { x, y };
};

const getNextAvailablePosition = (
  parentPosition,
  existingPositions,
  nodeWidth = 350,
  nodeHeight = 250,
  minSpacing = 200,
  parentNode = null
) => {
  // console.log('🎯 Smart Positioning Debug:', {
  //   parentPosition,
  //   existingCount: existingPositions.length,
  //   existingPositions,
  //   parentNodeExpanded: parentNode?.data?.isExpanded
  // });

  // Try each position in priority order
  for (const positionConfig of POSITION_PRIORITY) {
    const testPosition = calculatePositionCoordinates(
      parentPosition,
      positionConfig.direction,
      nodeWidth,
      nodeHeight,
      minSpacing,
      parentNode
    );

    const isAvailable = checkPositionAvailable(
      testPosition,
      existingPositions,
      nodeWidth,
      nodeHeight,
      minSpacing
    );

    // console.log(`🔍 Testing ${positionConfig.direction}:`, {
    //   testPosition,
    //   isAvailable,
    //   shimmer: positionConfig.shimmer
    // });

    if (isAvailable) {
      // console.log(`✅ Using ${positionConfig.direction} with ${positionConfig.shimmer} shimmer`);
      return {
        position: testPosition,
        direction: positionConfig.direction,
        shimmer: positionConfig.shimmer,
        sourceHandle: positionConfig.sourceHandle,
        targetHandle: positionConfig.targetHandle,
      };
    }
  }

  // Fallback: find rightmost position and place further right
  const rightmostX = Math.max(
    ...existingPositions.map((pos) => pos.x),
    parentPosition.x
  );
  const fallbackPosition = {
    x: rightmostX + nodeWidth + minSpacing + 50,
    y: parentPosition.y,
  };

  // console.log('🚨 Using fallback position:', fallbackPosition);
  return {
    position: fallbackPosition,
    direction: "right-extended",
    shimmer: "left-to-right",
    sourceHandle: "source-right",
    targetHandle: "target-left",
  };
};

// Utility function to calculate optimal handle pair based on node positions
const calculateOptimalHandles = (sourceNode, targetNode) => {
  if (!sourceNode || !targetNode)
    return { sourceHandle: "source-right", targetHandle: "target-left" };

  const sourceCenter = {
    x: sourceNode.position.x + 175, // Node width / 2
    y: sourceNode.position.y + 125, // Node height / 2
  };

  const targetCenter = {
    x: targetNode.position.x + 175,
    y: targetNode.position.y + 125,
  };

  // Calculate the vector from source to target
  const dx = targetCenter.x - sourceCenter.x;
  const dy = targetCenter.y - sourceCenter.y;

  // Determine primary direction based on which axis has larger difference
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);

  let sourceHandle, targetHandle;

  if (absX > absY) {
    // Horizontal connection is primary
    if (dx > 0) {
      // Target is to the right of source
      sourceHandle = "source-right";
      targetHandle = "target-left";
    } else {
      // Target is to the left of source
      sourceHandle = "source-left";
      targetHandle = "target-right";
    }
  } else {
    // Vertical connection is primary
    if (dy > 0) {
      // Target is below source
      sourceHandle = "source-bottom";
      targetHandle = "target-top";
    } else {
      // Target is above source
      sourceHandle = "source-top";
      targetHandle = "target-bottom";
    }
  }

  return { sourceHandle, targetHandle };
};

// Utility function to calculate biblical metadata from nodes
const calculateBiblicalMetadata = (nodes) => {
  const allBooks = new Set();
  const allVerses = new Set();

  nodes.forEach((node) => {
    if (node.data?.booksMentioned) {
      node.data.booksMentioned.forEach((book) => allBooks.add(book));
    }
    if (node.data?.versesReferenced) {
      node.data.versesReferenced.forEach((verse) => allVerses.add(verse));
    }
  });

  return {
    booksCount: allBooks.size,
    versesCount: allVerses.size,
    books: Array.from(allBooks),
    verses: Array.from(allVerses),
  };
};

export const useFlow = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [nodeIdCounter, setNodeIdCounter] = useState(1);
  const reactFlowInstance = useRef(null);
  const [lastKnownZoom, setLastKnownZoom] = useState(0.85); // Track last known zoom level
  const [firstNodeId, setFirstNodeId] = useState(null); // Track the first node in the workflow
  const [lastNodeId, setLastNodeId] = useState(null); // Track the last (most recent) node
  const [edgeType, setEdgeType] = useState('default'); // 'default' = Bezier curve (track edge type preference per workflow)

  // Workflow management state
  const [currentWorkflowName, setCurrentWorkflowName] = useState(null);
  const [currentFirestoreSessionId, setCurrentFirestoreSessionId] = useState(null); // Separate Firestore session ID
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedState, setLastSavedState] = useState(null);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
  const [forceUniqueTitle, setForceUniqueTitle] = useState(false);
  const sessionCreationInProgress = useRef(false); // Prevent double session creation

  // Autosave state
  const [lastAutosaveTime, setLastAutosaveTime] = useState(null);
  const [showRecoveryPrompt, setShowRecoveryPrompt] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const AUTOSAVE_KEY = "bible_workflow_autosave";

  // Refs to track latest values without causing re-renders
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const nodeIdCounterRef = useRef(nodeIdCounter);
  const edgeTypeRef = useRef(edgeType);

  // Keep refs in sync with latest values
  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;
    nodeIdCounterRef.current = nodeIdCounter;
    edgeTypeRef.current = edgeType;
  }, [nodes, edges, nodeIdCounter, edgeType]);

  // Track changes to nodes and edges
  useEffect(() => {
    // Skip expensive change detection during workflow loading
    if (isLoadingWorkflow) {
      console.log("⏸️ Skipping change detection during workflow load");
      return;
    }

    if (lastSavedState) {
      const currentState = JSON.stringify({ nodes, edges });
      const savedState = JSON.stringify(lastSavedState);
      const hasChanges = currentState !== savedState;
      setHasUnsavedChanges(hasChanges);
      console.log("🔍 Change detection (with saved state):", {
        hasChanges,
        nodeCount: nodes.length,
        edgeCount: edges.length,
      });
    } else if (nodes.length > 0 || edges.length > 0) {
      setHasUnsavedChanges(true);
      console.log("🔍 Change detection (no saved state):", {
        hasUnsavedChanges: true,
        nodeCount: nodes.length,
        edgeCount: edges.length,
      });
    } else {
      console.log("🔍 Change detection (no changes):", {
        nodeCount: nodes.length,
        edgeCount: edges.length,
      });
    }
  }, [nodes, edges, lastSavedState, isLoadingWorkflow]);

  // Helper function to generate autosave title
  const generateAutosaveTitle = useCallback((firstQuestion) => {
    if (!firstQuestion) return "Bible Study Session";

    // Clean the question and truncate to 40 characters, but preserve the natural question format
    let title = firstQuestion
      .replace(/[?!]/g, "") // Remove punctuation
      .trim();

    // Capitalize first letter if it's not already
    if (title.length > 0) {
      title = title.charAt(0).toUpperCase() + title.slice(1);
    }

    if (title.length > 40) {
      // Cut at word boundary if possible
      const lastSpace = title.lastIndexOf(" ", 37);
      title =
        lastSpace > 20
          ? title.substring(0, lastSpace) + "..."
          : title.substring(0, 37) + "...";
    }

    return title || "Bible Study Session";
  }, []);

  // Helper function to generate unique title when starting fresh exploration
  const generateUniqueTitle = useCallback(async (baseTitle, existingWorkflows = null) => {
    console.log('🔢 generateUniqueTitle called');
    console.log('  baseTitle:', baseTitle);

    // CRITICAL FIX: Always fetch ALL workflows from BOTH sources (Firestore + IndexedDB)
    // to prevent duplicate titles across storage systems
    let allWorkflows = [];

    // Load from Firestore if user is authenticated
    const user = auth.currentUser;
    if (user) {
      try {
        const firestoreWorkflows = await getAllWorkflowsFromFirestore(user.uid);
        console.log('  📊 Firestore workflows:', firestoreWorkflows.length, firestoreWorkflows.map(w => w.name));
        allWorkflows = [...allWorkflows, ...firestoreWorkflows];
      } catch (error) {
        console.error('  ❌ Failed to load Firestore workflows:', error);
      }
    }

    // Load from IndexedDB
    try {
      const { default: storageService } = await import("../services/storageService.js");
      const indexedDBWorkflows = await storageService.getAllWorkflows();
      console.log('  📊 IndexedDB workflows:', indexedDBWorkflows.length, indexedDBWorkflows.map(w => w.name));
      allWorkflows = [...allWorkflows, ...indexedDBWorkflows];
    } catch (error) {
      console.error('  ❌ Failed to load IndexedDB workflows:', error);
    }

    // Remove duplicates by name (in case a workflow exists in both places)
    const uniqueWorkflowNames = new Set(allWorkflows.map(w => w.name));
    console.log('  📊 Total unique workflow names:', uniqueWorkflowNames.size, Array.from(uniqueWorkflowNames));

    // If no workflows exist with this base title, use it as-is
    if (!uniqueWorkflowNames.has(baseTitle)) {
      console.log('  ✅ No conflict, using base title');
      return baseTitle;
    }

    // Generate numbered variants until we find one that doesn't exist
    let counter = 2;
    let uniqueTitle;

    do {
      uniqueTitle = `${baseTitle} (${counter})`;
      console.log(`  🔍 Checking if "${uniqueTitle}" exists...`);
      counter++;
    } while (uniqueWorkflowNames.has(uniqueTitle));

    console.log(`  ✅ Generated unique title: "${uniqueTitle}"`);
    return uniqueTitle;
  }, []);

  // Helper function to perform autosave using storage service
  const performAutosave = useCallback(async () => {
    const user = auth.currentUser;

    // If user is authenticated and has a Firestore session, save to Firestore
    if (user && currentFirestoreSessionId) {
      console.log('💾 Autosaving node positions to Firestore...');
      const currentNodes = nodesRef.current;

      if (currentNodes.length > 0) {
        try {
          const { updateSessionNodes } = await import("../services/firestoreService.js");
          await updateSessionNodes(user.uid, currentFirestoreSessionId, currentNodes);
          setHasUnsavedChanges(false);
          setLastSavedState({ nodes: currentNodes, edges: edgesRef.current });
          console.log("✅ Firestore autosave completed");
        } catch (error) {
          console.error("❌ Firestore autosave failed:", error);
        }
      }
      return;
    }

    // If user is authenticated but no Firestore session yet, skip (session will be created on first node)
    if (user) {
      console.log('⏭️ Skipping autosave (Firestore session not yet created)');
      return;
    }

    // Get latest values from refs
    const currentNodes = nodesRef.current;
    const currentEdges = edgesRef.current;
    const currentNodeIdCounter = nodeIdCounterRef.current;
    const currentEdgeType = edgeTypeRef.current;

    if (currentNodes.length > 0) {
      try {
        // Find the VERY FIRST node (root node) to use as title
        // Priority: 1) node with isFirstNode flag, 2) node with lowest sequenceNumber, 3) any node with question
        const firstNode =
          currentNodes.find((n) => n.data?.isFirstNode && n.data?.question) ||
          currentNodes.reduce((earliest, node) => {
            if (!node.data?.question) return earliest;
            if (!earliest) return node;
            const nodeSeq = node.data?.sequenceNumber || Infinity;
            const earlySeq = earliest.data?.sequenceNumber || Infinity;
            return nodeSeq < earlySeq ? node : earliest;
          }, null) ||
          currentNodes.find((n) => n.data?.question);

        const firstQuestion =
          firstNode?.data?.question || "Bible Study Session";
        const title = generateAutosaveTitle(firstQuestion);

        // Calculate biblical metadata
        const biblicalMetadata = calculateBiblicalMetadata(currentNodes);

        const workflowData = {
          nodes: currentNodes,
          edges: currentEdges,
          nodeIdCounter: currentNodeIdCounter,
          edgeType: currentEdgeType,
          ...biblicalMetadata,
        };

        console.log('💾 Saving workflow with edgeType:', currentEdgeType);

        // Import storage service dynamically
        const { default: storageService } = await import(
          "../services/storageService.js"
        );

        // Check if this exact title already exists
        const existingWorkflows = await storageService.getAllWorkflows();
        const existingWorkflow = existingWorkflows.find(
          (w) => w.name === title
        );

        let finalTitle = title;

        if (existingWorkflow) {
          if (forceUniqueTitle) {
            // Generate a unique title for fresh exploration (checks ALL sources internally)
            finalTitle = await generateUniqueTitle(title);
            await storageService.saveWorkflow(finalTitle, workflowData);
            // console.log(`💾 Created fresh exploration: "${finalTitle}"`);
            setForceUniqueTitle(false); // Reset flag
          } else {
            // Update existing workflow (continue previous research)
            await storageService.updateWorkflow(title, workflowData);
            // console.log(`💾 Updated autosave: "${title}"`);
          }
        } else {
          // Create new workflow
          await storageService.saveWorkflow(finalTitle, workflowData);
          // console.log(`💾 Autosaved new: "${finalTitle}"`);
        }

        setLastAutosaveTime(Date.now());
        setCurrentWorkflowName(finalTitle); // Set current workflow name
        // Reset unsaved changes flag after autosave
        setHasUnsavedChanges(false);
        setLastSavedState({ nodes: currentNodes, edges: currentEdges });
        console.log("✅ Autosave completed successfully:", finalTitle);
      } catch (error) {
        console.error("❌ Autosave failed:", error);
      }
    }
  }, [generateAutosaveTitle, generateUniqueTitle, forceUniqueTitle, currentFirestoreSessionId]);

  // Autosave immediately when there are unsaved changes (with debouncing for position changes)
  useEffect(() => {
    // Skip autosave during workflow loading to prevent hanging
    if (isLoadingWorkflow) {
      console.log("⏸️ Skipping autosave during workflow load");
      return;
    }

    if (hasUnsavedChanges && nodes.length > 0) {
      console.log("🔄 Changes detected, autosaving with debounce:", {
        nodeCount: nodes.length,
        edgeCount: edges.length,
        hasUnsavedChanges,
      });

      // Debounce autosave to avoid excessive saves during dragging
      const timeoutId = setTimeout(() => {
        console.log("💾 Executing autosave now...");
        performAutosave();
      }, 500); // Save 500ms after the last change

      return () => {
        console.log("🚫 Clearing previous autosave timeout");
        clearTimeout(timeoutId);
      };
    }
  }, [
    hasUnsavedChanges,
    nodes,
    edges,
    performAutosave,
    isLoadingWorkflow,
  ]);

  // Check for recovery on mount
  useEffect(() => {
    const checkRecovery = async () => {
      try {
        const autosaved = await storageHelper.getItem(AUTOSAVE_KEY);
        if (autosaved && nodes.length === 0) {
          const data = JSON.parse(autosaved);
          const timeAgo = getTimeAgo(data.timestamp);

          setRecoveryData({
            title: data.title,
            timeAgo,
            data,
          });
          setShowRecoveryPrompt(true);
          // console.log(`🔄 Found autosave: "${data.title}" from ${timeAgo}`);
        }
      } catch (error) {
        // console.error("Recovery check failed:", error);
        await storageHelper.removeItem(AUTOSAVE_KEY);
      }
    };

    checkRecovery();
  }, []); // Only run on mount

  // Helper function to format time ago
  const getTimeAgo = useCallback((timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return `${days} day${days > 1 ? "s" : ""} ago`;
  }, []);

  // Function to update edge handles based on current node positions
  const updateEdgeHandles = useCallback(() => {
    // Skip edge updates during workflow loading to prevent hanging
    if (isLoadingWorkflow) {
      // console.log('⏸️ Skipping edge handle updates during workflow load');
      return;
    }

    setEdges((currentEdges) => {
      return currentEdges.map((edge) => {
        const sourceNode = nodes.find((n) => n.id === edge.source);
        const targetNode = nodes.find((n) => n.id === edge.target);

        if (sourceNode && targetNode) {
          const { sourceHandle, targetHandle } = calculateOptimalHandles(
            sourceNode,
            targetNode
          );

          // Only update if handles have changed
          if (
            edge.sourceHandle !== sourceHandle ||
            edge.targetHandle !== targetHandle
          ) {
            return {
              ...edge,
              sourceHandle,
              targetHandle,
            };
          }
        }

        return edge;
      });
    });
  }, [nodes, setEdges, isLoadingWorkflow]);

  // Custom nodes change handler that triggers edge updates
  const customOnNodesChange = useCallback(
    (changes) => {
      console.log("📍 Node changes detected:", changes);

      // Apply the node changes first
      onNodesChange(changes);

      // Check if any changes involve node dragging/positioning
      const hasPositionChanges = changes.some(
        (change) =>
          change.type === "position" ||
          (change.type === "drag" && change.dragging === false)
      );

      if (hasPositionChanges) {
        console.log("🎯 Position changes detected, updating edge handles");
        // Update edge handles after a short delay to ensure node state is updated
        requestAnimationFrame(() => {
          updateEdgeHandles();
        });
      }
    },
    [onNodesChange, updateEdgeHandles]
  );

  // Z-index management for bringing nodes to front
  const bringNodeToFront = useCallback(
    (nodeId) => {
      if (!nodeId) return;

      console.log("🎯 bringNodeToFront called with nodeId:", nodeId);

      setNodes((currentNodes) => {
        const updatedNodes = currentNodes.map((node) => {
          if (node.id === nodeId) {
            console.log("🎯 Setting node", nodeId, "zIndex to 1000");
            // Bring selected node to front with high z-index (but below newly created nodes at 9999)
            return {
              ...node,
              zIndex: 1000,
            };
          } else if (node.zIndex === 1000) {
            console.log("🎯 Resetting node", node.id, "zIndex to 1");
            // Reset previously selected nodes to default z-index
            return {
              ...node,
              zIndex: 1,
            };
          } else if (node.zIndex === 9999) {
            // NEVER reset newly created nodes (9999) - they should stay on top
            console.log("🎯 Preserving new node", node.id, "zIndex at 9999");
            return node;
          } else if (node.type === "responseNode" && !node.zIndex) {
            // Ensure all response nodes have a default z-index
            return {
              ...node,
              zIndex: 1,
            };
          }
          return node;
        });

        console.log("🎯 Updated nodes with z-index:", updatedNodes.map(n => ({ id: n.id, zIndex: n.zIndex })));
        return updatedNodes;
      });
    },
    [setNodes]
  );

  // Set ReactFlow instance reference
  const setReactFlowInstance = useCallback(
    (instance) => {
      reactFlowInstance.current = instance;
      // console.log("ReactFlow instance set:", !!instance, instance);

      // Log viewport info and center on existing nodes when instance is set
      if (instance) {
        setTimeout(() => {
          try {
            const viewport = instance.getViewport();
            // console.log("Initial viewport:", viewport);

            // Save the current zoom level
            if (viewport && viewport.zoom) {
              setLastKnownZoom(viewport.zoom);
            }

            // Skip automatic fitView to prevent hanging on workflow load
            // Users can manually fit view using the controls or workflow loading will handle it
            const responseNodes = nodes.filter(
              (node) => node.type === "responseNode"
            );
            // console.log(`ReactFlow initialized with ${responseNodes.length} existing nodes`);
          } catch (error) {
            console.error("Error getting initial viewport:", error);
          }
        }, 200);
      }
    },
    [nodes]
  );

  // Enhanced focus function for any node
  const focusNode = useCallback(
    (nodeId, nodePosition, duration = 800, customZoom = null) => {
      if (!reactFlowInstance.current) {
        // console.warn("ReactFlow instance not available for focusing");
        return;
      }

      // Use provided position if available, otherwise find the node
      let x, y;
      if (nodePosition) {
        x = nodePosition.x + 224; // Center point (max-w-md = 448px / 2)
        y = nodePosition.y + 125; // Center point (approximate height 250/2)
      } else {
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) {
          // console.warn(`Node ${nodeId} not found for focusing`);
          return;
        }

        // Use ReactFlow's measured dimensions if available (most accurate)
        if (node.measured?.width && node.measured?.height) {
          x = node.position.x + node.measured.width / 2;
          y = node.position.y + node.measured.height / 2;
        } else if (node.width && node.height) {
          // Fallback to node dimensions if measured not available
          x = node.position.x + node.width / 2;
          y = node.position.y + node.height / 2;
        } else {
          // Final fallback to max-w-md dimensions (448px)
          x = node.position.x + 224; // max-w-md = 448px / 2
          y = node.position.y + 200; // approximate height for expanded nodes
        }
      }

      // console.log(`Focusing on node ${nodeId} at center position (${x}, ${y})`);

      try {
        // Get current viewport
        const viewport = reactFlowInstance.current.getViewport();
        // console.log("Current viewport before centering:", viewport);

        // Use custom zoom if provided (for first node), otherwise use current zoom or saved zoom level
        const zoomToUse = customZoom || viewport?.zoom || lastKnownZoom;

        // Update stored zoom level
        if (customZoom) {
          setLastKnownZoom(customZoom);
        } else if (viewport?.zoom) {
          setLastKnownZoom(viewport.zoom);
        }

        // console.log("Using zoom level:", zoomToUse);

        // Use setCenter with proper coordinates and specified zoom
        reactFlowInstance.current.setCenter(x, y, {
          zoom: zoomToUse,
          duration: duration,
        });

        // Log viewport after centering
        setTimeout(() => {
          const newViewport = reactFlowInstance.current.getViewport();
          // console.log("New viewport after centering:", newViewport);
        }, duration + 100);
      } catch (error) {
        // console.error("Error centering view:", error);

        // Fallback: use fitView to the specific node
        try {
          const currentViewport = reactFlowInstance.current.getViewport();
          const zoomToUse = currentViewport?.zoom || lastKnownZoom;

          reactFlowInstance.current.fitView({
            nodes: [{ id: nodeId }],
            duration: duration,
            padding: 0.3,
            maxZoom: zoomToUse,
            minZoom: 0.5,
          });
        } catch (fallbackError) {
          // console.error("Fallback centering also failed:", fallbackError);
        }
      }

      // Enhanced highlight effect function
      const addHighlightEffect = () => {
        const nodeElement = document.querySelector(`[data-id="${nodeId}"]`);
        if (nodeElement) {
          nodeElement.style.transition =
            "transform 0.4s ease, box-shadow 0.4s ease";
          nodeElement.style.transform = "scale(1.02)";
          nodeElement.style.boxShadow =
            "0 10px 25px rgba(59, 130, 246, 0.3), 0 0 0 2px rgba(59, 130, 246, 0.1)";

          // Remove the highlight after animation
          setTimeout(() => {
            nodeElement.style.transform = "scale(1)";
            nodeElement.style.boxShadow = "none";
            setTimeout(() => {
              nodeElement.style.transition = "";
            }, 400);
          }, 1600);
        }
      };

      // Apply highlight effect with improved timing
      requestAnimationFrame(() => {
        setTimeout(addHighlightEffect, duration + 100);
      });
    },
    [nodes]
  );

  // Add a new response node
  const addResponseNode = useCallback(
    (responseData, parentNodeId = null, isFromHighlight = false) => {
      const newNodeId = `response-${nodeIdCounter}`;
      setNodeIdCounter((prev) => prev + 1);

      // console.log(
      //   "Adding new node:",
      //   newNodeId,
      //   "ReactFlow available:",
      //   !!reactFlowInstance.current,
      //   "isFromHighlight:",
      //   isFromHighlight
      // );

      // Calculate position for new node with enhanced collision detection
      const calculatePosition = () => {
        const nodeWidth = 350;
        const nodeHeight = 250;
        const minSpacing = 200;

        // Helper function to check if two rectangles overlap (with spacing buffer)
        const isOverlapping = (pos1, pos2) => {
          return !(
            pos1.x + nodeWidth + minSpacing <= pos2.x ||
            pos2.x + nodeWidth + minSpacing <= pos1.x ||
            pos1.y + nodeHeight + minSpacing <= pos2.y ||
            pos2.y + nodeHeight + minSpacing <= pos1.y
          );
        };

        // Get all existing node positions
        const existingPositions = nodes
          .filter((node) => node.type === "responseNode")
          .map((node) => node.position);

        // Enhanced positioning around parent node (for ANY node with a parent, not just highlights)
        if (parentNodeId) {
          const parentNode = nodes.find((n) => n.id === parentNodeId);
          if (parentNode) {
            // console.log(
            //   "Positioning near parent node:",
            //   parentNode.id,
            //   "at:",
            //   parentNode.position
            // );

            const findClearPositionAroundParent = (parentPosition) => {
              // Check if smart positioning data is available
              if (responseData.smartPositioning) {
                // console.log('🚀 Using smart positioning:', responseData.smartPositioning);
                // console.log('🚀 Bypassing all legacy logic for smart positioning');
                return responseData.smartPositioning.position;
              }

              // console.log('⚠️ No smart positioning data, using legacy logic');
              // Legacy positioning logic for backward compatibility
              // Check if this is from an Ask button (always place below)
              const isFromAskButton = responseData.isFromAskButton;
              // Check if this is from Reflect and Pray button (always place to the left)
              const isReflectAndPray = responseData.isReflectAndPray;

              if (isReflectAndPray) {
                // Calculate position to the left of parent node
                const leftPosition = {
                  x: parentPosition.x - (nodeWidth + minSpacing),
                  y: parentPosition.y, // Same vertical level as parent
                };

                // Check if this position overlaps with any existing node
                const hasOverlap = existingPositions.some((existingPos) =>
                  isOverlapping(leftPosition, existingPos)
                );

                if (!hasOverlap) {
                  // console.log(
                  //   "Placing Reflect and Pray node directly to the left of parent"
                  // );
                  return leftPosition;
                }

                // If direct left is occupied, try alternative left positions
                const alternativeOffsets = [
                  { x: -(nodeWidth + minSpacing), y: -80 }, // Slightly above
                  { x: -(nodeWidth + minSpacing), y: 80 }, // Slightly below
                  { x: -(nodeWidth + minSpacing * 1.5), y: 0 }, // Further left
                  { x: -(nodeWidth + minSpacing * 1.5), y: -80 }, // Further left, above
                  { x: -(nodeWidth + minSpacing * 1.5), y: 80 }, // Further left, below
                ];

                for (const offset of alternativeOffsets) {
                  const testPosition = {
                    x: parentPosition.x + offset.x,
                    y: parentPosition.y + offset.y,
                  };

                  const hasOverlap = existingPositions.some((existingPos) =>
                    isOverlapping(testPosition, existingPos)
                  );

                  if (!hasOverlap) {
                    // console.log(
                    //   "Placing Reflect and Pray node to the left with offset:",
                    //   offset
                    // );
                    return testPosition;
                  }
                }

                // Fallback: place further to the left
                return {
                  x: parentPosition.x - (nodeWidth + minSpacing * 2),
                  y: parentPosition.y,
                };
              }

              if (isFromAskButton) {
                // Calculate dynamic spacing based on parent node's actual current state
                const parentNode = nodes.find((n) => n.id === parentNodeId);
                const isParentExpanded = parentNode?.data?.isExpanded || false;

                // Calculate actual content height based on parent's response length and expansion state
                const calculateActualHeight = (node) => {
                  if (!node?.data?.response) return nodeHeight; // Default height if no content

                  const responseText = node.data.response;
                  const lineCount = responseText.split("\n").length;
                  const estimatedLineHeight = 24; // Increased line height for better accuracy
                  const headerHeight = 90; // Height of node header (increased)
                  const paddingAndMargin = 80; // Internal padding and margins (increased)
                  const askButtonHeight = 60; // Height of Ask button section (increased)

                  // Calculate source links section height
                  const calculateSourceLinksHeight = (nodeData) => {
                    const booksCount = nodeData?.booksMentioned?.length || 0;
                    const versesCount = nodeData?.versesReferenced?.length || 0;
                    const totalSources = booksCount + versesCount;

                    if (totalSources === 0) return 0; // No source section

                    // Source section components:
                    const sectionSpacing = 25; // border-t pt-2 mt-3 mb-2
                    const labelHeight = 20; // "📖 Sources:" label
                    const buttonHeight = 35; // Height per button
                    const gap = 8; // gap-2 between buttons

                    // Estimate rows (assuming ~3-4 buttons per row based on typical lengths)
                    const buttonsPerRow = 3;
                    const rows = Math.ceil(totalSources / buttonsPerRow);
                    const buttonsHeight =
                      rows * buttonHeight + (rows - 1) * gap;

                    return sectionSpacing + labelHeight + buttonsHeight + 15; // +15px bottom padding
                  };

                  // Source links are ALWAYS visible regardless of expand/collapse state
                  const sourceLinksHeight = calculateSourceLinksHeight(
                    node.data
                  );

                  if (isParentExpanded) {
                    // For expanded nodes, calculate based on actual full content with extra buffer
                    const contentHeight = lineCount * estimatedLineHeight;
                    const bufferHeight = 100; // Extra buffer for formatting, lists, etc.
                    return (
                      headerHeight +
                      contentHeight +
                      paddingAndMargin +
                      askButtonHeight +
                      sourceLinksHeight +
                      bufferHeight
                    );
                  } else {
                    // For collapsed nodes, use preview height (5 lines max) + sources are still visible
                    const previewLines = Math.min(lineCount, 5);
                    const contentHeight = previewLines * estimatedLineHeight;
                    return (
                      headerHeight +
                      contentHeight +
                      paddingAndMargin +
                      askButtonHeight +
                      sourceLinksHeight
                    );
                  }
                };

                const actualParentHeight = calculateActualHeight(parentNode);

                // For collapsed nodes, use calculated height + spacing instead of fixed 450px
                const dynamicSpacing = isParentExpanded
                  ? actualParentHeight + minSpacing + 200 // Expanded: calculated + safety margin
                  : actualParentHeight + minSpacing + 50; // Collapsed: calculated + smaller margin

                // console.log(
                //   `Parent node ${parentNodeId} - Expanded: ${isParentExpanded}, Calculated height: ${actualParentHeight}px, Spacing: ${dynamicSpacing}px`
                // );
                // console.log(
                //   `Source links always visible - Books: ${
                //     parentNode?.data?.booksMentioned?.length || 0
                //   }, Verses: ${parentNode?.data?.versesReferenced?.length || 0}`
                // );

                // Place directly below with dynamic spacing based on actual height
                const bottomPosition = {
                  x: parentPosition.x,
                  y: parentPosition.y + dynamicSpacing,
                };

                // Check if this position overlaps with any existing node
                const hasOverlap = existingPositions.some((existingPos) =>
                  isOverlapping(bottomPosition, existingPos)
                );

                if (!hasOverlap) {
                  // console.log(
                  //   "Placing Ask button node directly below parent with dynamic spacing"
                  // );
                  return bottomPosition;
                }

                // If direct bottom is occupied, try slightly offset positions with same dynamic spacing
                const alternativeOffsets = [
                  { x: 50, y: dynamicSpacing }, // Slightly right
                  { x: -50, y: dynamicSpacing }, // Slightly left
                  { x: 100, y: dynamicSpacing }, // More right
                  { x: -100, y: dynamicSpacing }, // More left
                ];

                for (const offset of alternativeOffsets) {
                  const testPosition = {
                    x: parentPosition.x + offset.x,
                    y: parentPosition.y + offset.y,
                  };

                  const hasOverlap = existingPositions.some((existingPos) =>
                    isOverlapping(testPosition, existingPos)
                  );

                  if (!hasOverlap) {
                    // console.log(
                    //   "Placing Ask button node below with offset:",
                    //   offset
                    // );
                    return testPosition;
                  }
                }

                // Fallback: place further below with extra margin
                return {
                  x: parentPosition.x,
                  y: parentPosition.y + dynamicSpacing + minSpacing,
                };
              }

              // Special positioning for source nodes (books and verses) - Enhanced Dynamic Positioning
              const isFromSource = responseData.isFromSource;

              if (isFromSource) {
                // console.log(
                //   `Positioning source node (${responseData.sourceType}: ${responseData.sourceName}) using dynamic positioning`
                // );

                // Calculate dynamic top position based on parent's actual state
                const calculateDynamicTopPosition = () => {
                  const parentNode = nodes.find((n) => n.id === parentNodeId);
                  if (!parentNode) return SOURCE_POSITIONING_CONFIG.topOffset;

                  const isParentExpanded = parentNode.data?.isExpanded || false;
                  let dynamicTopOffset = SOURCE_POSITIONING_CONFIG.topOffset;

                  // Adjust based on parent expansion state
                  if (isParentExpanded) {
                    // For expanded nodes, calculate based on estimated content height
                    const responseText = parentNode.data?.response || "";
                    const lineCount = responseText.split("\n").length;
                    const estimatedContentHeight = lineCount * 24; // 24px per line estimate

                    // Add buffer for expanded content, but cap it for very long content
                    const contentBuffer = Math.min(
                      estimatedContentHeight * 0.3,
                      SOURCE_POSITIONING_CONFIG.expandedParentBuffer * 2
                    );
                    dynamicTopOffset =
                      SOURCE_POSITIONING_CONFIG.topOffset + contentBuffer;

                    // console.log(
                    //   `Parent expanded with ${lineCount} lines, adding ${contentBuffer}px buffer`
                    // );
                  } else {
                    // For collapsed nodes, use base offset (already below middle) with minimal adjustment
                    dynamicTopOffset = SOURCE_POSITIONING_CONFIG.topOffset + 20; // Slight downward adjustment for better readability
                  }

                  return Math.max(
                    dynamicTopOffset,
                    SOURCE_POSITIONING_CONFIG.topOffset
                  ); // Ensure minimum offset
                };

                const dynamicTopOffset = calculateDynamicTopPosition();
                const horizontalOffset =
                  SOURCE_POSITIONING_CONFIG.horizontalOffset;
                const verticalSpacing =
                  SOURCE_POSITIONING_CONFIG.verticalSpacing;

                // Enhanced source positioning with dynamic calculations
                const sourceOffsets = [
                  {
                    x: -(nodeWidth + horizontalOffset),
                    y: dynamicTopOffset,
                    name: "left-dynamic-primary",
                  },
                  {
                    x: -(nodeWidth + horizontalOffset),
                    y: dynamicTopOffset + verticalSpacing,
                    name: "left-dynamic-secondary",
                  },
                  {
                    x: -(nodeWidth + horizontalOffset * 1.2),
                    y: dynamicTopOffset,
                    name: "left-wide-primary",
                  },
                  {
                    x: -(nodeWidth + horizontalOffset),
                    y: dynamicTopOffset + verticalSpacing * 2,
                    name: "left-dynamic-tertiary",
                  },
                  {
                    x: -(nodeWidth + horizontalOffset * 1.2),
                    y: dynamicTopOffset + verticalSpacing,
                    name: "left-wide-secondary",
                  },
                  {
                    x: -(nodeWidth + horizontalOffset * 1.4),
                    y: dynamicTopOffset,
                    name: "left-extra-wide",
                  },
                ];

                // Test each position with enhanced collision detection
                for (const offset of sourceOffsets) {
                  const testPosition = {
                    x: parentPosition.x + offset.x,
                    y: parentPosition.y + offset.y,
                  };

                  // Enhanced collision detection with configurable clearance
                  const hasOverlap = existingPositions.some((existingPos) => {
                    const clearanceBuffer =
                      SOURCE_POSITIONING_CONFIG.minClearance;
                    return !(
                      testPosition.x + nodeWidth + clearanceBuffer <
                        existingPos.x ||
                      existingPos.x + nodeWidth + clearanceBuffer <
                        testPosition.x ||
                      testPosition.y + nodeHeight + clearanceBuffer <
                        existingPos.y ||
                      existingPos.y + nodeHeight + clearanceBuffer <
                        testPosition.y
                    );
                  });

                  if (!hasOverlap) {
                    // console.log(
                    //   `Placing source node at ${offset.name}: x=${testPosition.x}, y=${testPosition.y}`
                    // );
                    return testPosition;
                  }
                }

                // Enhanced fallback with spiral search pattern
                // console.log(
                //   "All source positions occupied, using spiral search fallback"
                // );
                const baseX = parentPosition.x - (nodeWidth + horizontalOffset);
                const baseY = parentPosition.y + dynamicTopOffset;

                // Spiral search for available position
                for (
                  let radius = verticalSpacing * 0.5;
                  radius <= verticalSpacing * 3;
                  radius += verticalSpacing * 0.3
                ) {
                  for (let angle = 0; angle < 360; angle += 30) {
                    const radian = (angle * Math.PI) / 180;
                    const testPosition = {
                      x: baseX + Math.cos(radian) * radius * 0.5, // Horizontal bias
                      y: baseY + Math.sin(radian) * radius,
                    };

                    const hasOverlap = existingPositions.some((existingPos) =>
                      isOverlapping(testPosition, existingPos)
                    );

                    if (!hasOverlap) {
                      // console.log(
                      //   `Found spiral position at radius ${radius}, angle ${angle}`
                      // );
                      return testPosition;
                    }
                  }
                }

                // Ultimate fallback
                return {
                  x: baseX - horizontalOffset * 0.5,
                  y: baseY + verticalSpacing * 3,
                };
              }

              // Default positioning for non-Ask button nodes (Learn More, etc.)
              const offsets = [
                { x: nodeWidth + minSpacing, y: 0, name: "right" }, // Right (default priority)
                { x: 0, y: nodeHeight + minSpacing, name: "bottom" }, // Bottom
                { x: -(nodeWidth + minSpacing), y: 0, name: "left" }, // Left
                { x: 0, y: -(nodeHeight + minSpacing), name: "top" }, // Top
                {
                  x: nodeWidth + minSpacing,
                  y: nodeHeight + minSpacing,
                  name: "bottom-right",
                }, // Bottom-right
                {
                  x: -(nodeWidth + minSpacing),
                  y: nodeHeight + minSpacing,
                  name: "bottom-left",
                }, // Bottom-left
                {
                  x: nodeWidth + minSpacing,
                  y: -(nodeHeight + minSpacing),
                  name: "top-right",
                }, // Top-right
                {
                  x: -(nodeWidth + minSpacing),
                  y: -(nodeHeight + minSpacing),
                  name: "top-left",
                }, // Top-left
              ];

              for (const offset of offsets) {
                const testPosition = {
                  x: parentPosition.x + offset.x,
                  y: parentPosition.y + offset.y,
                };

                const hasOverlap = existingPositions.some((existingPos) =>
                  isOverlapping(testPosition, existingPos)
                );

                if (!hasOverlap) {
                  // console.log(
                  //   `Found clear position ${offset.name} of parent:`,
                  //   testPosition
                  // );
                  return testPosition;
                }
              }

              // If no clear position found, try expanding radius around parent in concentric circles
              const searchRadius = nodeWidth + minSpacing;
              for (
                let radius = searchRadius * 1.2;
                radius <= searchRadius * 5;
                radius += searchRadius * 0.5
              ) {
                for (let angle = 0; angle < 360; angle += 20) {
                  // More granular angle search
                  const radian = (angle * Math.PI) / 180;
                  const testPosition = {
                    x: parentPosition.x + Math.cos(radian) * radius,
                    y: parentPosition.y + Math.sin(radian) * radius,
                  };

                  const hasOverlap = existingPositions.some((existingPos) =>
                    isOverlapping(testPosition, existingPos)
                  );

                  if (!hasOverlap) {
                    // console.log(
                    //   "Found clear position at radius:",
                    //   radius,
                    //   "angle:",
                    //   angle,
                    //   "position:",
                    //   testPosition
                    // );
                    return testPosition;
                  }
                }
              }

              // Ultimate fallback: find the rightmost node and place to its right
              const rightmostX = Math.max(
                ...existingPositions.map((pos) => pos.x),
                parentPosition.x
              );
              return {
                x: rightmostX + nodeWidth + minSpacing + 50,
                y: parentPosition.y,
              };
            };

            return findClearPositionAroundParent(parentNode.position);
          }
        }

        // Default positioning (viewport center) for non-highlight nodes or when parent not found
        const getCurrentViewportCenter = () => {
          // For the very first node, use viewport center position
          if (nodes.length === 0) {
            // console.log("First node - calculating viewport center");
            // Calculate center based on viewport dimensions
            const viewportWidth = window.innerWidth || 1280;
            const viewportHeight = window.innerHeight || 720;
            // Position node at center (accounting for node dimensions)
            const nodeWidth = 350;
            const nodeHeight = 250;
            const centerX = (viewportWidth - nodeWidth) / 2;
            const centerY = (viewportHeight - nodeHeight) / 2;
            // console.log(`First node position: x=${centerX}, y=${centerY}`);
            return { x: centerX, y: centerY };
          }

          // For subsequent nodes, try to get viewport center
          if (!reactFlowInstance.current) {
            // console.log(
            //   "ReactFlow instance not ready, using screen center fallback"
            // );
            const centerX = (window.innerWidth || 1280) / 2;
            const centerY = (window.innerHeight || 720) / 2;
            return { x: centerX, y: centerY };
          }

          try {
            const viewport = reactFlowInstance.current.getViewport();
            const viewportWidth = window.innerWidth || 1280;
            const viewportHeight = window.innerHeight || 720;

            // Check if viewport is in initial state (zoom = 1, x = 0, y = 0)
            if (viewport.zoom === 1 && viewport.x === 0 && viewport.y === 0) {
              // console.log("Viewport in initial state, using screen center");
              const centerX = viewportWidth / 2;
              const centerY = viewportHeight / 2;
              return { x: centerX, y: centerY };
            }

            // Calculate the center point of the current viewport in flow coordinates
            const centerX =
              -viewport.x / viewport.zoom + viewportWidth / 2 / viewport.zoom;
            const centerY =
              -viewport.y / viewport.zoom + viewportHeight / 2 / viewport.zoom;

            // console.log("Viewport calculation:", {
            //   viewport,
            //   viewportWidth,
            //   viewportHeight,
            //   centerX,
            //   centerY,
            // });

            // Ensure we don't return negative coordinates or NaN
            if (!isFinite(centerX) || !isFinite(centerY)) {
              // console.warn(
              //   "Invalid viewport center calculated, using fallback"
              // );
              return { x: viewportWidth / 2, y: viewportHeight / 2 };
            }

            return { x: centerX, y: centerY };
          } catch (error) {
            // console.error("Error calculating viewport center:", error);
            const centerX = (window.innerWidth || 1280) / 2;
            const centerY = (window.innerHeight || 720) / 2;
            return { x: centerX, y: centerY };
          }
        };

        // Find clear position around viewport center with enhanced collision detection
        const findClearPositionAroundCenter = (centerPosition) => {
          // console.log(
          //   "Positioning node at center:",
          //   centerPosition,
          //   "with",
          //   existingPositions.length,
          //   "existing nodes"
          // );

          // If no existing nodes, use viewport center
          if (existingPositions.length === 0) {
            return centerPosition;
          }

          // Check if viewport center is clear
          const hasOverlapAtCenter = existingPositions.some((existingPos) =>
            isOverlapping(centerPosition, existingPos)
          );

          if (!hasOverlapAtCenter) {
            // console.log("Center position is clear, using:", centerPosition);
            return centerPosition;
          }

          // console.log("Center position overlaps, searching for alternative...");

          // If center is occupied, find nearest clear position in expanding circles
          const searchRadius = Math.max(nodeWidth + minSpacing, 120);

          for (
            let radius = searchRadius * 0.8;
            radius <= searchRadius * 6;
            radius += searchRadius * 0.4
          ) {
            // Try 16 positions around the center in a circle (more granular)
            for (let angle = 0; angle < 360; angle += 22.5) {
              const radian = (angle * Math.PI) / 180;
              const testPosition = {
                x: centerPosition.x + Math.cos(radian) * radius,
                y: centerPosition.y + Math.sin(radian) * radius,
              };

              const hasOverlap = existingPositions.some((existingPos) =>
                isOverlapping(testPosition, existingPos)
              );

              if (!hasOverlap) {
                // console.log(
                //   "Found clear position at radius:",
                //   radius,
                //   "angle:",
                //   angle,
                //   "position:",
                //   testPosition
                // );
                return testPosition;
              }
            }
          }

          // Enhanced fallback: place in a grid pattern if circular search fails
          const gridSpacing = nodeWidth + minSpacing;
          for (let gridX = 0; gridX < 10; gridX++) {
            for (let gridY = 0; gridY < 10; gridY++) {
              const testPosition = {
                x: centerPosition.x + (gridX - 5) * gridSpacing,
                y: centerPosition.y + (gridY - 5) * gridSpacing,
              };

              const hasOverlap = existingPositions.some((existingPos) =>
                isOverlapping(testPosition, existingPos)
              );

              if (!hasOverlap) {
                // console.log("Found clear position in grid:", testPosition);
                return testPosition;
              }
            }
          }

          // Ultimate fallback: place to the right of rightmost node
          const rightmostX = Math.max(...existingPositions.map((pos) => pos.x));
          const fallbackPosition = {
            x: rightmostX + nodeWidth + minSpacing + 50,
            y: centerPosition.y,
          };
          // console.log("Using ultimate fallback position:", fallbackPosition);
          return fallbackPosition;
        };

        // Get viewport center and position node there
        const viewportCenter = getCurrentViewportCenter();
        const finalPosition = findClearPositionAroundCenter(viewportCenter);

        // console.log("Final node position:", finalPosition);
        return finalPosition;
      };

      // Resolve parent node ID for transferred workflows (same logic as addHighlightedTextNode)
      if (parentNodeId && !nodes.find(n => n.id === parentNodeId)) {
        // Try to find by originalId for transferred nodes
        if (/^[0-9]+$/.test(parentNodeId)) {
          const resolvedParent = nodes.find(n => n.data?.originalId === parentNodeId);
          if (resolvedParent) {
            parentNodeId = resolvedParent.id; // Update to new ID
            console.log(`🔄 Resolved parent ID: ${parentNodeId} → ${resolvedParent.id}`);
          }
        }
      }

      // Check if this is the first response node (not counting input nodes)
      const existingResponseNodes = nodes.filter(node => node.type === "responseNode");
      const isFirstNode = existingResponseNodes.length === 0 && !parentNodeId;

      // Set first node ID if this is the first node
      if (isFirstNode && !firstNodeId) {
        setFirstNodeId(newNodeId);
      }

      // Only update the current last node to clear its isLastNode flag
      setNodes((currentNodes) => {
        const currentLastNodeIndex = currentNodes.findIndex(node => node.data?.isLastNode);

        if (currentLastNodeIndex === -1) {
          return currentNodes;
        }

        return currentNodes.map((node, index) => {
          if (index === currentLastNodeIndex) {
            console.log(`📝 Clearing isLastNode flag from previous last node ${node.id}`);
            return {
              ...node,
              data: {
                ...node.data,
                isLastNode: false,
                sequenceNumber: node.data.sequenceNumber || (index + 1)
              }
            };
          }
          return node;
        });
      });

      // Set this as the new last node
      setLastNodeId(newNodeId);

      // Prepare node data for API response structure
      const nodeData = {
        // Handle API response structure
        response:
          responseData.response?.response ||
          responseData.response ||
          responseData.summary?.summary ||
          responseData.summary ||
          "",
        actions: responseData.response?.actions || responseData.actions || [],
        question:
          responseData.response?.question ||
          responseData.question ||
          "Session Summary",
        nodeId: newNodeId,
        isSummary: !!responseData.summary,
        interactionId: responseData.interactionId,
        // Add reflection flag for special handling
        isReflection: responseData.isReflection || responseData.response?.isReflection,
        isReflectAndPray: responseData.isReflectAndPray,
        isExplore: responseData.isExplore, // Add Explore/Discover flag
        isContinueJourney: responseData.isContinueJourney, // Add Continue Journey flag
        isFirstNode: isFirstNode, // Mark as first node
        isLastNode: true, // Mark as last node (most recent)
        sequenceNumber: nodeIdCounter, // Add sequence number for creation order
        // Add books and verses data for biblical metrics tracking
        booksMentioned:
          responseData.response?.booksMentioned ||
          responseData.booksMentioned ||
          [],
        versesReferenced:
          responseData.response?.versesReferenced ||
          responseData.versesReferenced ||
          [],
      };

      const nodePosition = calculatePosition();
      // Special styling for source nodes
      const isSourceNode = responseData.isFromSource;
      const nodeStyle = isSourceNode
        ? {
            background: "transparent",
            border: "none",
            filter: "drop-shadow(0 4px 12px rgba(139, 90, 43, 0.15))", // Subtle brown shadow for source nodes
          }
        : {
            background: "transparent",
            border: "none",
          };

      const newNode = {
        id: newNodeId,
        type: "responseNode",
        position: nodePosition,
        data: nodeData,
        style: nodeStyle,
        dragHandle: ".drag-handle",
        zIndex: 9999, // New nodes start at front with very high z-index
      };

      // CRITICAL: Single atomic state update to prevent z-index race conditions
      // Reset nodes with high z-index (9999) back to normal, reset manually-focused nodes (1000) to 1
      // Then add new node with zIndex: 9999
      // This ensures the new node is ALWAYS on top, even if parent is currently selected/focused
      setNodes((currentNodes) => {
        const resetNodes = currentNodes.map(node => ({
          ...node,
          // Reset any previously "new" nodes (9999) or manually-focused nodes (1000) back to default
          zIndex: (node.zIndex === 9999 || node.zIndex === 1000) ? 1 : node.zIndex
        }));
        return [...resetNodes, newNode]; // Add new node with zIndex: 9999 (already set above)
      });

      // Save to Firestore if user is authenticated
      (async () => {
        try {
          const user = auth.currentUser;
          console.log('🔍 Firestore Save Check:', {
            isAuthenticated: !!user,
            userId: user?.uid,
            currentFirestoreSessionId,
            currentWorkflowName,
            newNodeId
          });

          if (user) {
            // Get or create session ID (use separate Firestore session state)
            let sessionId = currentFirestoreSessionId;

            // If no session exists yet, create one (with mutex to prevent double creation)
            if (!sessionId) {
              // Check if another async call is already creating a session
              if (sessionCreationInProgress.current) {
                console.log('⏳ Session creation already in progress, waiting...');
                // Wait for the other creation to finish (max 5 seconds)
                const startTime = Date.now();
                while (!currentFirestoreSessionId && (Date.now() - startTime) < 5000) {
                  await new Promise(resolve => setTimeout(resolve, 100));
                }
                sessionId = currentFirestoreSessionId;

                if (!sessionId) {
                  console.warn('⚠️ Timed out waiting for session creation, creating new session anyway');
                }
              }

              // Double-check currentFirestoreSessionId wasn't set while waiting
              if (!sessionId && !currentFirestoreSessionId) {
                sessionCreationInProgress.current = true;
                try {
                  // CRITICAL: Always use the FIRST node's question for the session title
                  // If this is the first node, use its question. Otherwise, find the first node from existing nodes
                  let firstQuestion;
                  if (nodeData.isFirstNode) {
                    // This is the first node, use its question
                    firstQuestion = nodeData.question || "Bible Study Session";
                  } else {
                    // This is a follow-up node, find the actual first node's question
                    const currentNodes = nodesRef.current;
                    const actualFirstNode =
                      currentNodes.find((n) => n.data?.isFirstNode && n.data?.question) ||
                      currentNodes.reduce((earliest, node) => {
                        if (!node.data?.question) return earliest;
                        if (!earliest) return node;
                        const nodeSeq = node.data?.sequenceNumber || Infinity;
                        const earlySeq = earliest.data?.sequenceNumber || Infinity;
                        return nodeSeq < earlySeq ? node : earliest;
                      }, null) ||
                      currentNodes.find((n) => n.data?.question);

                    firstQuestion = actualFirstNode?.data?.question || nodeData.question || "Bible Study Session";
                  }

                  let title = generateAutosaveTitle(firstQuestion);

                  console.log('🔍 DEBUG: About to create Firestore session');
                  console.log('  isFirstNode:', nodeData.isFirstNode);
                  console.log('  current node question:', nodeData.question);
                  console.log('  firstQuestion (for title):', firstQuestion);
                  console.log('  generated title:', title);

                  // ALWAYS generate a unique title to prevent duplicates
                  title = await generateUniqueTitle(title);
                  console.log(`📝 Creating new Firestore session with unique title: "${title}"`);
                  setForceUniqueTitle(false); // Reset flag if it was set

                  sessionId = await createSession(user.uid, title);
                  setCurrentFirestoreSessionId(sessionId); // Store session ID separately
                  console.log(`✅ Created new Firestore session: ${sessionId} with title: "${title}"`);
                } finally {
                  sessionCreationInProgress.current = false;
                }
              } else {
                sessionId = sessionId || currentFirestoreSessionId;
                console.log(`📂 Using session created by parallel call: ${sessionId}`);
              }
            } else {
              console.log(`📂 Using existing Firestore session: ${sessionId}`);
            }

            // Prepare node data for Firestore with parentId
            const firestoreNodeData = {
              id: newNodeId,
              position: nodePosition,
              data: {
                ...nodeData,
                parentId: parentNodeId || null
              }
            };

            console.log(`💾 Saving node ${newNodeId} to Firestore...`);
            // Save node to Firestore
            await saveNode(user.uid, sessionId, firestoreNodeData);
            console.log(`✅ Successfully saved node ${newNodeId} to Firestore session ${sessionId}`);
          } else {
            console.warn('⚠️ User not authenticated - skipping Firestore save');
          }
        } catch (error) {
          console.error('❌ Failed to save node to Firestore:', error);
          console.error('Error details:', {
            code: error.code,
            message: error.message,
            stack: error.stack
          });
          // Don't throw - allow app to continue even if Firestore save fails
        }
      })();

      // Center viewport on the new node instantly (no animation)
      setTimeout(() => {
        if (reactFlowInstance.current) {
          try {
            // Get current zoom level before centering
            const currentViewport = reactFlowInstance.current.getViewport();

            // Update stored zoom level
            if (currentViewport?.zoom) {
              setLastKnownZoom(currentViewport.zoom);
            }

            // Get the node from ReactFlow's internal state
            const allNodes = reactFlowInstance.current.getNodes();
            const targetNode = allNodes.find((n) => n.id === newNodeId);

            if (targetNode) {
              // Calculate center point of the node (node width: 350px, height: 250px)
              const centerX = targetNode.position.x + 175; // Center of 350px wide node
              const centerY = targetNode.position.y + 125; // Center of 250px tall node

              // Center viewport instantly without specifying zoom to prevent jerking at max zoom
              // setCenter() without zoom parameter maintains current zoom smoothly
              reactFlowInstance.current.setCenter(centerX, centerY);

              // console.log(
              //   "Centered viewport on node:",
              //   newNodeId,
              //   "at:",
              //   centerX,
              //   centerY
              // );
            }
          } catch (error) {
            console.error("Error centering viewport:", error);
          }
        }
      }, 100); // Short delay to ensure node is rendered

      // Add animated edge if there's a parent node
      if (parentNodeId && parentNodeId !== "input-node") {
        // Calculate optimal handles based on node positions
        const parentNode = nodes.find((n) => n.id === parentNodeId);
        const currentNode = { id: newNodeId, position: nodePosition };
        const { sourceHandle, targetHandle } = calculateOptimalHandles(
          parentNode,
          currentNode
        );

        // Special styling for source nodes (books/verses) vs regular connections
        const isSourceNode = responseData.isFromSource;
        const edgeStyle = isSourceNode
          ? {
              stroke: "#8b5a2b", // Earthy brown for reference connections
              strokeWidth: "2",
              strokeDasharray: "8 4", // Dashed line for references
              filter: "drop-shadow(0 2px 4px rgba(139, 90, 43, 0.3))",
            }
          : {
              stroke: "#d4af37", // Golden for regular connections
              strokeWidth: "2",
              filter: "drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3))",
            };

        const markerStyle = isSourceNode
          ? {
              type: "arrowclosed",
              color: "#8b5a2b", // Brown arrow for references
              width: 18,
              height: 18,
            }
          : {
              type: "arrowclosed",
              color: "#d4af37", // Golden arrow for regular
              width: 20,
              height: 20,
            };

        const newEdge = {
          id: `edge-${parentNodeId}-${newNodeId}`,
          source: parentNodeId,
          target: newNodeId,
          sourceHandle,
          targetHandle,
          type: "smoothstep",
          animated: !isSourceNode, // Source edges are not animated (more subtle)
          style: edgeStyle,
          markerEnd: markerStyle,
          data: {
            animationDuration: 2000,
            createdAt: Date.now(),
          },
        };

        // Add edge with a slight delay for animation effect
        setTimeout(() => {
          setEdges((currentEdges) => [...currentEdges, newEdge]);
        }, 300);
      }

      return newNodeId;
    },
    [nodes, nodeIdCounter, setNodes, setEdges, focusNode, currentFirestoreSessionId, setCurrentFirestoreSessionId, generateAutosaveTitle, sessionCreationInProgress, currentWorkflowName, firstNodeId, generateUniqueTitle]
  );

  // Add highlighted text node (connected to parent) with smart positioning
  const addHighlightedTextNode = useCallback(
    (responseData, parentNodeId) => {
      // console.log("🎯 addHighlightedTextNode called:", {
      //   action: responseData.question,
      //   parentNodeId,
      //   totalNodes: nodes.length,
      //   isExplore: responseData.isExplore,
      //   isContinueJourney: responseData.isContinueJourney,
      // });

      // Use smart positioning for new nodes
      const existingPositions = nodes
        .filter((node) => node.type === "responseNode")
        .map((node) => node.position);

      // console.log("📍 Existing positions:", existingPositions);

      // Try to find parent node by exact ID first
      let parentNode = nodes.find((n) => n.id === parentNodeId);

      // If not found and parentNodeId is a simple ID (like "1", "2"),
      // try to find by originalId (for transferred nodes)
      if (!parentNode && /^[0-9]+$/.test(parentNodeId)) {
        parentNode = nodes.find((n) => n.data?.originalId === parentNodeId);
        console.log(`🔄 Node ID resolution: ${parentNodeId} → ${parentNode?.id || 'not found'}`);
      }

      let smartPositioning = null;

      if (parentNode) {
        // console.log("👨‍👩‍👧‍👦 Parent node found:", parentNode.position);

        smartPositioning = getNextAvailablePosition(
          parentNode.position,
          existingPositions,
          350, // node width
          250, // node height
          200, // min spacing
          parentNode // pass parent node for dynamic height calculation
        );

        // console.log(
        //   `🎯 Smart positioning result for ${
        //     responseData.question || "unknown action"
        //   }:`,
        //   {
        //     direction: smartPositioning.direction,
        //     shimmer: smartPositioning.shimmer,
        //     position: smartPositioning.position,
        //   }
        // );
      } else {
        // console.log("❌ No parent node found for parentNodeId:", parentNodeId);
      }

      // Add the node with smart positioning data
      const enhancedResponseData = {
        ...responseData,
        smartPositioning: smartPositioning,
      };

      const newNodeId = addResponseNode(
        enhancedResponseData,
        parentNodeId,
        true
      );

      // Add a specific animated edge for highlighted text relationships with smart handles
      if (parentNode && parentNode.id !== "input-node") {
        // Use the resolved parent node ID for the edge
        const resolvedParentId = parentNode.id;

        // Use smart positioning handles if available, otherwise fallback to default handles
        const sourceHandle = smartPositioning?.sourceHandle || "bottom";
        const targetHandle = smartPositioning?.targetHandle || "top";

        const highlightEdge = {
          id: `highlight-edge-${resolvedParentId}-${newNodeId}`,
          source: resolvedParentId,
          target: newNodeId,
          sourceHandle: sourceHandle,
          targetHandle: targetHandle,
          type: "smoothstep",
          animated: true,
          style: {
            stroke: "#d4af37", // Consistent golden orange for all connections
            strokeWidth: responseData.isFromAskButton ? "3" : "2", // Subtle width variation
            strokeDasharray: "8,4", // Dashed for all connections
            filter: "drop-shadow(0 2px 6px rgba(212, 175, 55, 0.4))",
          },
          markerEnd: {
            type: "arrowclosed",
            color: "#d4af37", // Consistent golden orange
            width: 22,
            height: 22,
          },
          data: {
            animationDuration: 3000,
            createdAt: Date.now(),
            isHighlight: true,
            isFromAskButton: responseData.isFromAskButton,
            shimmerDirection: smartPositioning?.shimmer || "none",
            connectionDirection: smartPositioning?.direction || "below",
          },
        };

        // Add edge with animation delay
        setTimeout(() => {
          setEdges((currentEdges) => {
            // Remove any existing edge to avoid duplicates
            const filteredEdges = currentEdges.filter(
              (edge) => edge.id !== `edge-${parentNodeId}-${newNodeId}`
            );
            return [...filteredEdges, highlightEdge];
          });
        }, 500);
      }

      return newNodeId;
    },
    [addResponseNode, setEdges, nodes]
  );

  // Update input node visibility
  const toggleInputNode = useCallback(
    (visible) => {
      setNodes((currentNodes) =>
        currentNodes.map((node) =>
          node.id === "input-node" ? { ...node, hidden: !visible } : node
        )
      );
    },
    [setNodes]
  );

  // Clear all nodes except input
  const clearNodes = useCallback(() => {
    setNodes([initialNodes[0]]);
    setEdges([]);
    setNodeIdCounter(1);
  }, [setNodes, setEdges]);

  // Get node by ID
  const getNodeById = useCallback(
    (nodeId) => {
      return nodes.find((node) => node.id === nodeId);
    },
    [nodes]
  );

  // Update node data
  const updateNodeData = useCallback(
    (nodeId, newData) => {
      setNodes((currentNodes) =>
        currentNodes.map((node) =>
          node.id === nodeId
            ? { ...node, data: { ...node.data, ...newData } }
            : node
        )
      );
    },
    [setNodes]
  );

  // Auto-arrange nodes (simple force-directed layout)
  const autoArrangeNodes = useCallback(() => {
    const responseNodes = nodes.filter((node) => node.type === "responseNode");
    if (responseNodes.length <= 1) return;

    const arranged = responseNodes.map((node, index) => {
      const angle = (index / responseNodes.length) * 2 * Math.PI;
      const radius = Math.max(300, responseNodes.length * 50);

      return {
        ...node,
        position: {
          x: 400 + radius * Math.cos(angle),
          y: 300 + radius * Math.sin(angle),
        },
      };
    });

    setNodes((currentNodes) => [
      ...currentNodes.filter((node) => node.type !== "responseNode"),
      ...arranged,
    ]);
  }, [nodes, setNodes]);

  // Update single node expansion state
  const updateNodeExpansion = useCallback(
    (nodeId, isExpanded) => {
      setNodes((currentNodes) =>
        currentNodes.map((node) => {
          if (node.id === nodeId || node.data?.nodeId === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                isExpanded: isExpanded,
              },
            };
          }
          return node;
        })
      );
    },
    [setNodes]
  );

  // Collapse all expanded nodes
  const collapseAllNodes = useCallback(() => {
    // console.log("Collapsing all nodes...");
    setNodes((currentNodes) => {
      const updatedNodes = currentNodes.map((node) => {
        if (
          node.type === "responseNode" &&
          node.data?.response &&
          node.data.response.split("\n").length > 5
        ) {
          // console.log(`Collapsing node ${node.id}`);
          return {
            ...node,
            data: {
              ...node.data,
              isExpanded: false,
            },
          };
        }
        return node;
      });
      // console.log("Updated nodes:", updatedNodes);
      return updatedNodes;
    });
  }, [setNodes]);

  // Expand all collapsed nodes
  const expandAllNodes = useCallback(() => {
    // console.log("Expanding all nodes...");
    setNodes((currentNodes) => {
      const updatedNodes = currentNodes.map((node) => {
        if (
          node.type === "responseNode" &&
          node.data?.response &&
          node.data.response.split("\n").length > 5
        ) {
          // console.log(`Expanding node ${node.id}`);
          return {
            ...node,
            data: {
              ...node.data,
              isExpanded: true,
            },
          };
        }
        return node;
      });
      // console.log("Updated nodes:", updatedNodes);
      return updatedNodes;
    });
  }, [setNodes]);

  // Get count of expanded nodes
  const getExpandedNodesCount = useCallback(() => {
    const expandableNodes = nodes.filter(
      (node) =>
        node.type === "responseNode" &&
        node.data?.response &&
        node.data.response.split("\n").length > 5
    );

    const expandedNodes = expandableNodes.filter(
      (node) => node.data?.isExpanded === true
    );

    // console.log(`Expanded nodes: ${expandedNodes.length} / ${expandableNodes.length} expandable`);
    return expandedNodes.length;
  }, [nodes]);

  // Save current state
  const saveState = useCallback(() => {
    const state = { nodes, edges };
    setLastSavedState(state);
    setHasUnsavedChanges(false);
    return state;
  }, [nodes, edges]);

  // Load workflow state
  const loadWorkflow = useCallback(
    (workflowData) => {
      console.log("🚀 LOADWORKFLOW called during Learn More - this might affect START labels");
      setIsLoadingWorkflow(true);
      // console.log(
      //   "📊 Loading workflow with",
      //   workflowData?.nodes?.length || 0,
      //   "nodes",
      //   workflowData?.edges?.length || 0,
      //   "edges"
      // );

      try {
        // Load all nodes and edges at once using React.unstable_batchedUpdates to prevent cascading renders
        if (typeof React !== "undefined" && React.unstable_batchedUpdates) {
          React.unstable_batchedUpdates(() => {
            if (workflowData?.nodes) {
              // console.log(
              //   "📝 Setting nodes:",
              //   workflowData.nodes.length,
              //   "nodes"
              // );

              // Find and mark the first response node
              const processedNodes = [...workflowData.nodes];

              // Clear ALL existing isFirstNode and isLastNode flags before recalculating
              processedNodes.forEach(node => {
                if (node.data) {
                  delete node.data.isFirstNode;
                  delete node.data.isLastNode;
                }
              });

              const responseNodes = processedNodes.filter(n => n.type === 'responseNode');
              if (responseNodes.length > 0) {
                // Sort by ID timestamp (format: node-timestamp-random) or by simple numeric ID
                const sortedNodes = responseNodes.sort((a, b) => {
                  // Handle both timestamp format (node-timestamp-random) and simple numeric IDs
                  const getTimestamp = (id) => {
                    if (id.includes('-')) {
                      return parseInt(id.split('-')[1] || 0);
                    } else {
                      // For simple numeric IDs (like "1", "2", "3"), use the ID as timestamp
                      return parseInt(id) || 0;
                    }
                  };

                  const timestampA = getTimestamp(a.id);
                  const timestampB = getTimestamp(b.id);
                  return timestampA - timestampB;
                });

                // Assign sequence numbers to nodes that don't have them
                sortedNodes.forEach((node, index) => {
                  const nodeIndex = processedNodes.findIndex(n => n.id === node.id);
                  if (nodeIndex !== -1) {
                    // Assign sequence number if not present (1-based indexing)
                    if (!processedNodes[nodeIndex].data.sequenceNumber) {
                      processedNodes[nodeIndex].data.sequenceNumber = index + 1;
                    }
                  }
                });

                // Mark the first one
                const firstNode = sortedNodes[0];
                const firstNodeIndex = processedNodes.findIndex(n => n.id === firstNode.id);
                if (firstNodeIndex !== -1) {
                  processedNodes[firstNodeIndex].data.isFirstNode = true;
                  setFirstNodeId(firstNode.id);
                  // console.log("🌟 Marked first node:", firstNode.id);
                }

                // Find and mark the last (most recent) node - sort descending by timestamp
                const mostRecentNode = [...sortedNodes].sort((a, b) => {
                  const timestampA = parseInt(a.id.split('-')[1] || 0);
                  const timestampB = parseInt(b.id.split('-')[1] || 0);
                  return timestampB - timestampA; // Descending order
                })[0];

                const lastNodeIndex = processedNodes.findIndex(n => n.id === mostRecentNode.id);
                if (lastNodeIndex !== -1) {
                  processedNodes[lastNodeIndex].data.isLastNode = true;
                  setLastNodeId(mostRecentNode.id);
                  // console.log("🟢 Marked last node:", mostRecentNode.id);
                }
              }

              setNodes(processedNodes);
            }
            if (workflowData?.edges) {
              // console.log(
              //   "🔗 Setting edges:",
              //   workflowData.edges.length,
              //   "edges"
              // );
              setEdges(workflowData.edges);
            }
            if (workflowData?.nodeIdCounter) {
              setNodeIdCounter(workflowData.nodeIdCounter);
            }
            // Restore edge type preference (default to smoothstep if not saved)
            if (workflowData?.edgeType) {
              console.log('📥 Loading workflow with edgeType:', workflowData.edgeType);
              setEdgeType(workflowData.edgeType);
            } else {
              console.log('⚠️ No edgeType found in workflow data, using default');
            }
            // Set the Firestore session ID if present (important for follow-up questions)
            if (workflowData?.sessionId) {
              setCurrentFirestoreSessionId(workflowData.sessionId);
              console.log(`📂 Restored Firestore session ID: ${workflowData.sessionId}`);
            } else {
              console.log('⚠️ No sessionId found in workflowData:', workflowData);
            }
            setLastSavedState({
              nodes: workflowData.nodes,
              edges: workflowData.edges,
            });
            setHasUnsavedChanges(false);
          });
        } else {
          // Fallback for environments without batchedUpdates
          if (workflowData?.nodes) {
            // console.log(
            //   "📝 Setting nodes:",
            //   workflowData.nodes.length,
            //   "nodes"
            // );

            // Find and mark the first response node
            const processedNodes = [...workflowData.nodes];

            // Clear ALL existing isFirstNode and isLastNode flags before recalculating
            processedNodes.forEach(node => {
              if (node.data) {
                delete node.data.isFirstNode;
                delete node.data.isLastNode;
              }
            });

            const responseNodes = processedNodes.filter(n => n.type === 'responseNode');
            if (responseNodes.length > 0) {
              // Sort by ID timestamp (format: node-timestamp-random) or by simple numeric ID
              const sortedNodes = responseNodes.sort((a, b) => {
                // Handle both timestamp format (node-timestamp-random) and simple numeric IDs
                const getTimestamp = (id) => {
                  // Handle undefined/null IDs
                  if (!id) return 0;

                  // Convert to string to safely use includes
                  const idStr = String(id);

                  if (idStr.includes('-')) {
                    return parseInt(idStr.split('-')[1] || 0);
                  } else {
                    // For simple numeric IDs (like "1", "2", "3"), use the ID as timestamp
                    return parseInt(idStr) || 0;
                  }
                };

                const timestampA = getTimestamp(a.id);
                const timestampB = getTimestamp(b.id);
                return timestampA - timestampB;
              });

              // Assign sequence numbers to nodes that don't have them
              sortedNodes.forEach((node, index) => {
                const nodeIndex = processedNodes.findIndex(n => n.id === node.id);
                if (nodeIndex !== -1) {
                  // Assign sequence number if not present (1-based indexing)
                  if (!processedNodes[nodeIndex].data.sequenceNumber) {
                    processedNodes[nodeIndex].data.sequenceNumber = index + 1;
                  }
                }
              });

              // Mark the first one
              const firstNode = sortedNodes[0];
              const firstNodeIndex = processedNodes.findIndex(n => n.id === firstNode.id);
              if (firstNodeIndex !== -1) {
                processedNodes[firstNodeIndex].data.isFirstNode = true;
                setFirstNodeId(firstNode.id);
                // console.log("🌟 Marked first node:", firstNode.id);
              }

              // Find and mark the last (most recent) node - sort descending by timestamp
              const mostRecentNode = [...sortedNodes].sort((a, b) => {
                const timestampA = parseInt(a.id.split('-')[1] || 0);
                const timestampB = parseInt(b.id.split('-')[1] || 0);
                return timestampB - timestampA; // Descending order
              })[0];

              const lastNodeIndex = processedNodes.findIndex(n => n.id === mostRecentNode.id);
              if (lastNodeIndex !== -1) {
                processedNodes[lastNodeIndex].data.isLastNode = true;
                setLastNodeId(mostRecentNode.id);
                // console.log("🟢 Marked last node:", mostRecentNode.id);
              }
            }

            setNodes(processedNodes);
          }
          if (workflowData?.edges) {
            // console.log(
            //   "🔗 Setting edges:",
            //   workflowData.edges.length,
            //   "edges"
            // );
            setEdges(workflowData.edges);
          }
          if (workflowData?.nodeIdCounter) {
            setNodeIdCounter(workflowData.nodeIdCounter);
          }
          // Set the Firestore session ID if present (important for follow-up questions)
          if (workflowData?.sessionId) {
            setCurrentFirestoreSessionId(workflowData.sessionId);
            console.log(`📂 Restored Firestore session ID: ${workflowData.sessionId}`);
          }
          setLastSavedState({
            nodes: workflowData.nodes,
            edges: workflowData.edges,
          });
          setHasUnsavedChanges(false);
        }

        // Clear loading state after DOM updates
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // console.log("✅ Workflow data loaded, clearing loading state");
            setIsLoadingWorkflow(false);

            // Check if this is a default workflow (simple numeric IDs)
            // Safety check: ensure nodes array exists
            const isDefaultWorkflow = workflowData.nodes?.some(node =>
              /^[0-9]+$/.test(node.id)
            ) || false;

            if (isDefaultWorkflow) {
              // For default workflows, RESET viewport completely and center on the first node
              const firstNode = workflowData.nodes.find(node =>
                node.data?.isFirstNode === true
              );

              if (firstNode) {
                console.log("🎯 RESETTING viewport and centering on first node for default workflow:", firstNode.id);
                setTimeout(() => {
                  if (reactFlowInstance.current) {
                    try {
                      // FIRST: Reset viewport to initial state (zoom=1, x=0, y=0)
                      reactFlowInstance.current.setViewport({ x: 0, y: 0, zoom: 1 }, { duration: 0 });

                      // THEN: Position first node in upper portion with optimal zoom
                      setTimeout(() => {
                        const firstNodeCenterX = firstNode.position.x + 175; // Node center X (350px width / 2)
                        const firstNodeTopY = firstNode.position.y + 60;     // Show from top portion of node

                        reactFlowInstance.current.setCenter(firstNodeCenterX, firstNodeTopY, {
                          zoom: 1.2, // Optimal zoom level - not too zoomed in, not too zoomed out
                          duration: 1000
                        });
                      }, 100);
                    } catch (error) {
                      console.error("Error resetting viewport for default workflow:", error);
                      // Fallback: just try to focus on the node
                      if (focusNode) {
                        focusNode(firstNode.id, null, 1000, 1.5);
                      }
                    }
                  }
                }, 500);
              }
            } else {
              // For user workflows (saved workflows), always focus on first node with optimal positioning
              const firstNode = workflowData.nodes.find(node =>
                node.data?.isFirstNode === true
              );

              if (firstNode) {
                console.log("🎯 Focusing on first node for saved workflow:", firstNode.id);
                setTimeout(() => {
                  if (reactFlowInstance.current) {
                    try {
                      // Position first node in upper portion with optimal zoom for saved workflows
                      const firstNodeCenterX = firstNode.position.x + 175; // Node center X (350px width / 2)
                      const firstNodeTopY = firstNode.position.y + 60;     // Show from top portion of node

                      reactFlowInstance.current.setCenter(firstNodeCenterX, firstNodeTopY, {
                        zoom: 1.2, // Optimal zoom level for readability
                        duration: 1000
                      });
                    } catch (error) {
                      console.error("Error focusing on first node for saved workflow:", error);
                      // Fallback: use fitView for very small workflows
                      if (workflowData?.nodes?.length <= 3) {
                        reactFlowInstance.current.fitView({
                          padding: 0.2,
                          duration: 400,
                          minZoom: 1.2,
                          maxZoom: 1.5,
                          includeHiddenNodes: false,
                        });
                      }
                    }
                  }
                }, 500);
              } else if (workflowData?.nodes?.length <= 3) {
                // Fallback for workflows without marked first node and 3 or fewer nodes
                setTimeout(() => {
                  if (reactFlowInstance.current) {
                    try {
                      reactFlowInstance.current.fitView({
                        padding: 0.2,
                        duration: 400,
                        minZoom: 1.2,
                        maxZoom: 1.5,
                        includeHiddenNodes: false,
                      });
                    } catch (error) {
                      console.error("Error auto-fitting view:", error);
                    }
                  }
                }, 200);
              }
            }

            // Update edge handles once after loading is complete
            setTimeout(() => {
              // console.log("🔗 Updating edge handles after workflow load");
              updateEdgeHandles();
            }, 100);
          });
        });

        // Failsafe to ensure loading state is cleared
        setTimeout(() => {
          // console.log("🚨 Failsafe: Force clearing loading state");
          setIsLoadingWorkflow(false);
        }, 2000);
      } catch (error) {
        console.error("❌ Error loading workflow:", error);
        setIsLoadingWorkflow(false);
      }
    },
    [setNodes, setEdges, updateEdgeHandles]
  );

  // Reset workflow
  const resetWorkflow = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setNodeIdCounter(1);
    setFirstNodeId(null); // Reset first node tracking
    setLastNodeId(null); // Reset last node tracking
    setCurrentWorkflowName(null);
    setCurrentFirestoreSessionId(null); // Reset Firestore session ID
    setLastSavedState(null);
    setHasUnsavedChanges(false);
    storageHelper.removeItem(AUTOSAVE_KEY);
  }, [setNodes, setEdges, AUTOSAVE_KEY]);

  // DISABLED: Track viewport changes to update zoom level (causing infinite loops)
  const onViewportChange = useCallback(() => {
    // DISABLED - this was causing infinite loops during workflow loading
    // Viewport tracking is disabled to prevent hanging issues
  }, []);

  // Recovery functions
  const recoverAutosave = useCallback(() => {
    if (recoveryData?.data) {
      loadWorkflow(recoveryData.data);
      setCurrentWorkflowName(recoveryData.title);
      setShowRecoveryPrompt(false);
      storageHelper.removeItem(AUTOSAVE_KEY); // Clear autosave after recovery
      // console.log(`✅ Recovered session: "${recoveryData.title}"`);
    }
  }, [recoveryData, loadWorkflow, AUTOSAVE_KEY]);

  const dismissRecovery = useCallback(() => {
    setShowRecoveryPrompt(false);
    storageHelper.removeItem(AUTOSAVE_KEY); // Clear autosave when dismissed
    // console.log("❌ Autosave dismissed");
  }, [AUTOSAVE_KEY]);

  // Clear autosave after successful manual save
  const clearAutosave = useCallback(() => {
    storageHelper.removeItem(AUTOSAVE_KEY);
    setLastAutosaveTime(null);
  }, [AUTOSAVE_KEY]);

  // Transfer ownership of default workflow to authenticated user
  const transferWorkflowOwnership = useCallback((userId) => {
    console.log("🔄 Starting workflow ownership transfer for user:", userId);

    // 🛡️ LAYER 1: Check if user ID is valid
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      console.warn("❌ Invalid user ID provided:", userId);
      return { transferred: false, reason: "invalid_user_id" };
    }

    // 🛡️ LAYER 2: Check if current workflow has default workflow nodes (simple IDs like "1", "2", "3")
    const defaultNodes = nodes.filter(node =>
      node.type === "responseNode" && /^[0-9]+$/.test(node.id)
    );

    if (defaultNodes.length === 0) {
      console.log("ℹ️ No default workflow nodes found, skipping transfer");
      return { transferred: false, reason: "no_default_nodes" };
    }

    // 🛡️ LAYER 3: Check if nodes are already owned by this user
    const alreadyOwnedNodes = defaultNodes.filter(node =>
      node.data?.ownerId === userId
    );

    if (alreadyOwnedNodes.length === defaultNodes.length) {
      console.log("ℹ️ All default nodes already owned by this user, skipping transfer");
      return { transferred: false, reason: "already_owned" };
    }

    // 🛡️ LAYER 4: Check if there are any existing user-owned nodes with timestamp IDs
    const userTimestampNodes = nodes.filter(node =>
      node.type === "responseNode" &&
      node.data?.ownerId === userId &&
      /^node-\d+-[a-z0-9]+$/.test(node.id)
    );

    if (userTimestampNodes.length > 0) {
      console.log("⚠️ User already has owned nodes, checking for potential conflicts");

      // Allow transfer only if user explicitly initiated it and no recent transfers
      const recentTransfers = userTimestampNodes.filter(node =>
        node.data?.transferredAt &&
        (Date.now() - new Date(node.data.transferredAt).getTime()) < 5000 // 5 seconds
      );

      if (recentTransfers.length > 0) {
        console.warn("❌ Recent transfer detected, preventing duplicate transfer");
        return { transferred: false, reason: "recent_transfer_detected" };
      }
    }

    // 🛡️ LAYER 5: Final safety check for duplicate edges
    const existingEdgeIds = new Set(edges.map(edge => edge.id));

    console.log("🔍 Found default workflow nodes, proceeding with transfer");
    console.log(`📊 Transfer stats: ${defaultNodes.length} nodes, ${alreadyOwnedNodes.length} already owned`);

    // Generate new timestamp-based IDs for all nodes
    const idMap = new Map();
    const currentTime = Date.now();

    // Create mapping for all nodes that need new IDs
    nodes.forEach((node, index) => {
      if (node.type === "responseNode" && /^[0-9]+$/.test(node.id)) {
        const newId = `node-${currentTime + index}-${Math.random().toString(36).substr(2, 6)}`;
        idMap.set(node.id, newId);
        console.log(`📝 Mapping node ${node.id} → ${newId}`);
      }
    });

    // Transform nodes with new IDs and ownership
    const transformedNodes = nodes.map(node => {
      if (node.type === "responseNode" && idMap.has(node.id)) {
        const newId = idMap.get(node.id);

        // Preserve first/last node detection by checking original position
        const originalIndex = parseInt(node.id);
        const defaultNodeIds = nodes
          .filter(n => n.type === "responseNode" && /^[0-9]+$/.test(n.id))
          .map(n => parseInt(n.id))
          .sort((a, b) => a - b);

        const isFirstNode = originalIndex === defaultNodeIds[0];
        const isLastNode = originalIndex === defaultNodeIds[defaultNodeIds.length - 1];

        return {
          ...node,
          id: newId,
          data: {
            ...node.data,
            ownerId: userId,
            transferredAt: new Date().toISOString(),
            originalId: node.id,
            isFirstNode: isFirstNode,
            isLastNode: isLastNode,
          }
        };
      }
      return node;
    });

    // Transform edges to use new node IDs with duplicate protection
    const transformedEdges = edges.map(edge => {
      const newSourceId = idMap.get(edge.source) || edge.source;
      const newTargetId = idMap.get(edge.target) || edge.target;

      // Only update if either source or target changed
      if (newSourceId !== edge.source || newTargetId !== edge.target) {
        const newEdgeId = `edge-${newSourceId}-${newTargetId}`;

        // 🛡️ LAYER 5 ENFORCEMENT: Prevent duplicate edge IDs
        if (existingEdgeIds.has(newEdgeId)) {
          console.warn(`⚠️ Duplicate edge ID detected: ${newEdgeId}, adding suffix`);
          const timestamp = Date.now();
          const uniqueEdgeId = `edge-${newSourceId}-${newTargetId}-${timestamp}`;
          existingEdgeIds.add(uniqueEdgeId);

          return {
            ...edge,
            id: uniqueEdgeId,
            source: newSourceId,
            target: newTargetId,
            data: {
              ...edge.data,
              transferredAt: new Date().toISOString(),
              originalSource: edge.source,
              originalTarget: edge.target,
              hasDuplicateProtection: true,
            }
          };
        } else {
          existingEdgeIds.add(newEdgeId);
          return {
            ...edge,
            id: newEdgeId,
            source: newSourceId,
            target: newTargetId,
            data: {
              ...edge.data,
              transferredAt: new Date().toISOString(),
              originalSource: edge.source,
              originalTarget: edge.target,
            }
          };
        }
      }
      return edge;
    });

    // Apply the transformation
    console.log("✅ Applying ownership transfer transformation");
    setNodes(transformedNodes);
    setEdges(transformedEdges);

    // Update autosave with new ownership
    performAutosave();

    console.log("🎉 Workflow ownership transfer completed successfully");
    return {
      transferred: true,
      nodeCount: idMap.size,
      newIds: Array.from(idMap.values())
    };
  }, [nodes, edges, setNodes, setEdges, performAutosave]);

  // Method to force creation of a new exploration with unique title
  const startFreshExploration = useCallback(() => {
    setForceUniqueTitle(true);
  }, []);

  return {
    // ReactFlow state
    nodes,
    edges,
    onNodesChange: customOnNodesChange,
    onEdgesChange,
    onViewportChange,
    edgeType,
    setEdgeType,

    // Node management
    addResponseNode,
    addHighlightedTextNode,
    updateNodeData,
    getNodeById,

    // Layout management
    toggleInputNode,
    clearNodes,
    autoArrangeNodes,
    focusNode,
    setReactFlowInstance,
    collapseAllNodes,
    expandAllNodes,
    updateNodeExpansion,
    bringNodeToFront,

    // Workflow management
    currentWorkflowName,
    setCurrentWorkflowName,
    currentFirestoreSessionId,
    setCurrentFirestoreSessionId,
    hasUnsavedChanges,
    isLoadingWorkflow,
    saveState,
    loadWorkflow,
    resetWorkflow,
    nodeIdCounter,
    clearAutosave,
    transferWorkflowOwnership,

    // Recovery management
    showRecoveryPrompt,
    recoveryData,
    recoverAutosave,
    dismissRecovery,
    lastAutosaveTime,

    // Utilities
    nodeCount: nodes.filter((node) => node.type === "responseNode").length,
    getExpandedNodesCount,

    // Exploration management
    startFreshExploration,
  };
};

export default useFlow;
